// src/Dashboard/pages/user/DashboardUser/components/NotificationsPanel.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaBell,
  FaCheck,
  FaCheckDouble,
  FaEye,
  FaTrash,
} from "react-icons/fa";
import { MdMessage } from "react-icons/md";
import { toast } from "react-toastify";

import {
  deleteNotificationById,
  getNotificationsByUser,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../../../../services/notificationServices";
import { useNotification } from "../../../../../hooks/NotificationContext";

import DashboardDrawer from "./DashboardDrawer";
import EmptyState from "./EmptyState";
import {
  formatDateTimeFR,
  isReadFlag,
  sortByDateDesc,
} from "../utils/dashboardUserUtils";

export default function NotificationsPanel({ userId, onCounts }) {
  const { setUnreadCount } = useNotification();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const prevCountsRef = useRef({ total: -1, unread: -1 });

  const computeCounts = useCallback((list) => {
    const total = list.length;
    const unread = list.reduce(
      (acc, notification) =>
        acc + (isReadFlag(notification.read_notification) ? 0 : 1),
      0
    );

    return { total, unread };
  }, []);

  const pushCounts = useCallback(
    (list) => {
      const next = computeCounts(list);
      const prev = prevCountsRef.current;

      if (prev.total !== next.total || prev.unread !== next.unread) {
        prevCountsRef.current = next;
        onCounts?.(next);
        setUnreadCount?.(next.unread);
      }
    },
    [computeCounts, onCounts, setUnreadCount]
  );

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getNotificationsByUser(userId);
      const list = sortByDateDesc(
        Array.isArray(data) ? data : [],
        "created_at"
      );

      setNotifications(list);
      pushCounts(list);
    } catch (error) {
      console.error("[NotificationsPanel] load error:", error);
      toast.error("Impossible de charger les notifications.");
    } finally {
      setLoading(false);
    }
  }, [userId, pushCounts]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => computeCounts(notifications),
    [notifications, computeCounts]
  );

  const openNotification = (notification) => {
    setSelectedNotification(notification);
  };

  const toggleNotificationReadStatus = async (notificationId) => {
    const currentNotification = notifications.find(
      (notification) => notification.id === notificationId
    );

    if (!currentNotification) return;

    const nextReadValue = !isReadFlag(currentNotification.read_notification);

    try {
      await markNotificationAsRead(notificationId);

      const next = notifications.map((notification) =>
        notification.id === notificationId
          ? {
              ...notification,
              read_notification: nextReadValue,
            }
          : notification
      );

      setNotifications(next);
      pushCounts(next);

      setSelectedNotification((prev) =>
        prev?.id === notificationId
          ? {
              ...prev,
              read_notification: nextReadValue,
            }
          : prev
      );

      toast.success(
        nextReadValue
          ? "Notification marquée comme lue."
          : "Notification repassée en non lue."
      );
    } catch (error) {
      console.error("[NotificationsPanel] toggle read error:", error);
      toast.error("Impossible de modifier l’état de la notification.");
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm("Supprimer cette notification ?")) return;

    try {
      await deleteNotificationById(notificationId);

      const next = notifications.filter((item) => item.id !== notificationId);

      setNotifications(next);
      pushCounts(next);

      setSelectedNotification((prev) =>
        prev?.id === notificationId ? null : prev
      );

      toast.success("Notification supprimée.");
    } catch (error) {
      console.error("[NotificationsPanel] delete error:", error);
      toast.error("Impossible de supprimer la notification.");
    }
  };

  const markAllRead = async () => {
    try {
      const unreadIds = notifications
        .filter((notification) => !isReadFlag(notification.read_notification))
        .map((notification) => notification.id);

      if (!unreadIds.length) {
        toast.info("Toutes les notifications sont déjà lues.");
        return;
      }

      await markAllNotificationsAsRead(userId);

      const next = notifications.map((notification) => ({
        ...notification,
        read_notification: true,
      }));

      setNotifications(next);
      pushCounts(next);

      setSelectedNotification((prev) =>
        prev ? { ...prev, read_notification: true } : prev
      );

      toast.success("Toutes les notifications ont été marquées comme lues.");
    } catch (error) {
      console.error("[NotificationsPanel] mark all read error:", error);
      toast.error("Impossible de marquer les notifications comme lues.");
    }
  };

  const deleteAll = async () => {
    if (!notifications.length) return;
    if (!window.confirm("Supprimer toutes les notifications ?")) return;

    try {
      await Promise.all(
        notifications.map((notification) =>
          deleteNotificationById(notification.id)
        )
      );

      setNotifications([]);
      pushCounts([]);
      setSelectedNotification(null);

      toast.success("Toutes les notifications ont été supprimées.");
    } catch (error) {
      console.error("[NotificationsPanel] delete all error:", error);
      toast.error("Impossible de supprimer toutes les notifications.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!notifications.length) {
    return (
      <EmptyState
        icon={FaBell}
        title="Aucune notification"
        description="Les informations importantes du club apparaîtront ici."
      />
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-bold text-slate-900 dark:text-white">
            {counts.unread}
          </span>{" "}
          notification{counts.unread > 1 ? "s" : ""} non lue
          {counts.unread > 1 ? "s" : ""} sur {counts.total}.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={markAllRead}
            disabled={counts.unread === 0}
            className={[
              "inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition",
              counts.unread === 0
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-600"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
            ].join(" ")}
          >
            <FaCheckDouble />
            Tout marquer lu
          </button>

          <button
            type="button"
            onClick={deleteAll}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
          >
            <FaTrash />
            Tout supprimer
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {notifications.map((notification) => {
          const unread = !isReadFlag(notification.read_notification);
          const { date, time } = formatDateTimeFR(
            notification.created_at || notification.createdAt
          );

          return (
            <article
              key={notification.id}
              className={[
                "rounded-2xl border p-4 shadow-sm transition",
                unread
                  ? "border-rose-200 bg-rose-50/70 dark:border-rose-800/60 dark:bg-rose-900/20"
                  : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
              ].join(" ")}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {unread ? (
                      <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Non lue
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                        Lue
                      </span>
                    )}

                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {date} {time && `• ${time}`}
                    </span>
                  </div>

                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
                    {notification.message || "Aucun contenu."}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => openNotification(notification)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    title="Consulter"
                    aria-label="Consulter la notification"
                  >
                    <FaEye />
                    Voir
                  </button>

                  <button
                    type="button"
                    onClick={() => toggleNotificationReadStatus(notification.id)}
                    className={[
                      "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition",
                      unread
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40",
                    ].join(" ")}
                    title={unread ? "Marquer comme lue" : "Repasser en non lue"}
                    aria-label={
                      unread ? "Marquer comme lue" : "Repasser en non lue"
                    }
                  >
                    {unread ? <FaCheck /> : <MdMessage />}
                    {unread ? "Marquer lue" : "Repasser non lue"}
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteNotification(notification.id)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
                    title="Supprimer"
                    aria-label="Supprimer la notification"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <DashboardDrawer
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        title="Notification"
        subtitle="Détail de la notification"
        size="md"
      >
        {selectedNotification && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-900">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Date
                  </p>

                  <p className="mt-1 font-bold text-slate-900 dark:text-white">
                    {
                      formatDateTimeFR(
                        selectedNotification.created_at ||
                          selectedNotification.createdAt
                      ).date
                    }
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {!isReadFlag(selectedNotification.read_notification) ? (
                      <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Non lue
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                        Lue
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    toggleNotificationReadStatus(selectedNotification.id)
                  }
                  className={[
                    "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition",
                    !isReadFlag(selectedNotification.read_notification)
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40",
                  ].join(" ")}
                >
                  {!isReadFlag(selectedNotification.read_notification) ? (
                    <>
                      <FaCheck />
                      Marquer lue
                    </>
                  ) : (
                    <>
                      <MdMessage />
                      Repasser non lue
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                Notification
              </p>

              <p className="whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-300">
                {selectedNotification.message || "Aucun contenu."}
              </p>
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() =>
                  toggleNotificationReadStatus(selectedNotification.id)
                }
                className={[
                  "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition",
                  !isReadFlag(selectedNotification.read_notification)
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40",
                ].join(" ")}
              >
                {!isReadFlag(selectedNotification.read_notification) ? (
                  <>
                    <FaCheck />
                    Marquer comme lue
                  </>
                ) : (
                  <>
                    <MdMessage />
                    Repasser en non lue
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => deleteNotification(selectedNotification.id)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          </div>
        )}
      </DashboardDrawer>
    </>
  );
}